import {
  mqMin,
  tok,
  tokPx,
  zIndex,
} from '@sunwebgroup/ui-components'
import {
  Button,
  focusOutline,
  Icon,
  LazyImage,
  Tag,
  Typography,
} from '@sunwebgroup/ui-components/uds'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import { buildImgSrc } from '@/helpers/image-url'
import { BookingTags } from '@/scopes/account/common/components'
import { buildBookingUrl } from '@/scopes/account/common/policies/bookingUrl'
import { getImageOrigin } from '@/scopes/account/common/utils/getImageOrigin'
import { Card } from '@/scopes/common/components/Card'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useBookingsOverviewStore } from '../../infrastructure/useBookingsOverviewStore'
import {
  getDaysUntilDeparture,
  getSelectedUpcomingBooking,
  isUpcomingBookingOnHolidays,
} from '../selectors'

const IMG_WIDTH = 600
const IMG_HEIGHT = 400

interface UpcomingBookingCardProps {
  className?: string
}

const ButtonStyled = styled(Button)`
  margin-top: auto;
  pointer-events: none;
`

const StyledCard = styled(Card)`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: initial;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    flex-direction: row;
  }

  &:hover ${ButtonStyled} {
    background-color: ${tok('semantics_buttons_primary_hover_background_color')};
    color: ${tok('semantics_buttons_primary_hover_content_color')};
  }
`

const Link = styled.a`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${zIndex.level2};

  ${focusOutline()}

  &:focus-visible {
    border-radius: ${tokPx('semantics_cards_corner_radius')};
  }
`

const Content = styled.div`
  padding: ${tokPx('primitives_space_20')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${tokPx('primitives_space_4')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    width: ${tokPx('primitives_area_size_320')};
    flex-shrink: 0;
  }
`

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${tokPx('primitives_area_size_240')};
  border-top-left-radius: ${tokPx('semantics_cards_corner_radius')};
  border-top-right-radius: ${tokPx('semantics_cards_corner_radius')};
  overflow: hidden;
  border-radius: ${tokPx('semantics_cards_corner_radius')} ${tokPx('semantics_cards_corner_radius')} 0 0;
  @media ${mqMin('primitives_breakpoint_m_min')} {
    min-height: ${tokPx('primitives_area_size_400')};
    border-radius: ${tokPx('semantics_cards_corner_radius')} 0 0 ${tokPx('semantics_cards_corner_radius')};
    height: 100%;
  }
`

const Subtitle = styled(Typography)`
  color: ${tok('semantics_content_secondary_color')};
`

const ListItemContainer = styled.div`
  margin: ${tokPx('primitives_space_20')} 0;
  display: flex;
  flex-direction: column;
  gap: ${tokPx('primitives_space_4')};
`

const ListItem = styled(Typography)`
  display: flex;
  align-items: center;
  gap: ${tokPx('primitives_space_4')};
`

const ContainerLabels = styled.div`
  position: absolute;
  top: ${tokPx('primitives_space_8')};
  left: ${tokPx('primitives_space_8')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${tokPx('primitives_space_4')};
  z-index: ${zIndex.level1};
`

export function UpcomingBookingCard({ className }: UpcomingBookingCardProps) {
  const siteName = useConfiguration('siteName')
  const upcomingBooking = useBookingsOverviewStore(getSelectedUpcomingBooking)
  const isOnHolidays = useBookingsOverviewStore(isUpcomingBookingOnHolidays)
  const daysUntilDeparture = useBookingsOverviewStore(getDaysUntilDeparture)
  const intl = useIntl()
  const rawImageSrc = upcomingBooking?.imageSrc || ''
  const imageSrc = rawImageSrc.split('?')[0]
  const imageOrigin = getImageOrigin(rawImageSrc)
  const placeholderSrc = buildImgSrc(
    imageSrc,
    IMG_WIDTH / 4,
    IMG_HEIGHT / 4,
    imageOrigin,
  )
  const optimizedImageSrc = buildImgSrc(
    imageSrc,
    IMG_WIDTH * 2,
    IMG_HEIGHT * 2,
    imageOrigin,
  )

  if (!upcomingBooking)
    return null
  return (
    <StyledCard className={className}>
      <Link
        href={buildBookingUrl(
          siteName,
          upcomingBooking.bookingNumber,
          upcomingBooking.showNewBookingDetailPage,
        )}
        aria-label={intl.formatMessage(
          {
            id: 'bookings_overview_view_booking_aria_label',
            defaultMessage: 'View booking {accommodationName}',
          },
          { accommodationName: upcomingBooking.accommodationName },
        )}
      />
      <ImageWrapper>
        <ContainerLabels>
          <BookingTags
            isTotallySnow={upcomingBooking.isTotallySnow}
            isPrimavera={upcomingBooking.isPrimavera}
            isAddedByYou={upcomingBooking.addedByYou}
            isOnHolidays={isOnHolidays}
            daysUntilDeparture={daysUntilDeparture}
            isGroupBooking={upcomingBooking.isGroupBooking}
            isMasterBooking={upcomingBooking.isMasterBooking}
            hasChanges={upcomingBooking.hasChanges}
          />
        </ContainerLabels>

        <LazyImage
          placeholderSrc={placeholderSrc}
          alt={upcomingBooking.accommodationName}
          src={optimizedImageSrc}
          width={IMG_WIDTH}
          height={IMG_HEIGHT}
        />
      </ImageWrapper>
      <Content>
        <Tag
          size="M"
          variant="informative-tinted"
        >
          {intl.formatMessage({ id: 'common_booking_number_label' }, { bookingNumber: upcomingBooking.bookingNumber })}
        </Tag>
        <Typography variant="display-f-regular" forwardedAs="h2">
          {upcomingBooking.accommodationName}
        </Typography>
        {upcomingBooking.destination && (
          <Subtitle variant="body-s-regular-tight" forwardedAs="p">
            {upcomingBooking.destination}
          </Subtitle>
        )}
        <ListItemContainer>
          <ListItem forwardedAs="div" variant="body-s-bold-tight">
            <Icon glyph="Calendar" size={20} />
            {upcomingBooking.period}
          </ListItem>
          <ListItem forwardedAs="div" variant="body-s-bold-tight">
            <Icon glyph="Clock" size={20} />
            {intl.formatMessage(
              {
                id: 'bookings_overview_duration_days',
                defaultMessage: '{duration, plural, one {# day} other {# days}}',
              },
              { duration: upcomingBooking.duration },
            )}
          </ListItem>
        </ListItemContainer>
        <ButtonStyled
          variant="primary"
          fullWidth
          tabIndex={-1}
        >
          {intl.formatMessage({ id: 'bookings_overview_view_cta' })}
        </ButtonStyled>
      </Content>
    </StyledCard>
  )
}
